<template>
    <div class="no-access-helper-wrapper">
        <div class="no-access-card">
            <section class="message-block">
                <div class="text" v-if="!authState.loggedIn">
                    <h2>
                        You must be signed in to access <span class="highlight">{{ props.resource }}</span>.
                    </h2>
                    <p class="pun-subheading">You're not wired in here yet…</p>
                    <p>
                        If you have an account, please
                        <primevue-button @click="triggerLogin">
                            Login
                            <font-awesome-icon :icon="['fas', 'right-to-bracket']" />
                        </primevue-button>
                    </p>
                    <p>
                        If you do not have an account, but would like access, please contact the
                        <primevue-button class="contact-button" text @click="openContactUs">Spark team</primevue-button>
                        to request it.
                    </p>
                </div>
                <div class="text" v-else-if="props.isOldEnactPage && isProduction">
                    <h2>This page currently lives on a separate platform: <a target="_blank" rel="noopener noreferrer" href="https://enact.lcp.energy/">Enact</a></h2>
                    <h3>It will be available on Spark soon!</h3>
                    <p class="pun-subheading">This Spark is on another grid…</p>
                    <a
                        href="https://enact.lcp.energy/"
                        class="p-component p-button plain-btn"
                        target="_blank"
                        rel="noopener"
                    >
                        <span class="button-text">Go to Enact</span>
                        <font-awesome-icon :icon="['fas', 'right-to-bracket']" />
                    </a>
                </div>
                <div class="text" v-else>
                    <h2>You currently don't have access to <b>{{ props.resource }}</b>.</h2>
                    <p v-if="props.noAccessMessage" v-html="props.noAccessMessage" />
                    <p>
                        To request access, please contact the
                        <primevue-button class="contact-button" text @click="openContactUs">Spark team</primevue-button>.
                    </p>
                </div>
            </section>
            <section class="character-block" aria-hidden="true">
                <font-awesome-icon :icon="['fas', 'lock']" class="lock-icon" />
                <div class="plug-boy-wrap">
                    <plug-boy />
                </div>
            </section>
            <section class="game-block">
                <p class="game-prompt">Locked out? The grid still needs defending…</p>
                <game-selector />
            </section>
        </div>
        <div class="logo-container desktop-only" v-if="!props.hideBackground">
            <img class="spark-lines" :src="sparkLines" alt="spark lines splash" />
            <img class="spark-logo" :src="sparkLogo" alt="spark logo" />
        </div>
        <contact-form v-model:visible="showContactUsModal" />
    </div>
</template>

<script setup lang="ts">
import { authState, login } from '@lcp/http-auth';
import PrimevueButton from 'primevue/button';
import PlugBoy from '@/components/layout/PlugBoy.vue';
import sparkLines from '@/assets/imgs/svgs/spark-lines.svg';
import sparkLogo from '@/assets/imgs/svgs/spark-logo.svg';
import GameSelector from '@/components/layout/GameSelector.vue';
import ContactForm from '@/components/deltaGpt/ContactForm.vue';
import { ref } from 'vue';

const showContactUsModal = ref<boolean>(false);

const props = defineProps<{
    resource: string;
    noAccessMessage?: string;
    hideBackground? : boolean;
    isOldEnactPage?: boolean;
}>();

const isProduction = import.meta.env.MODE === 'production';

function triggerLogin (): void {
    login();
}

function openContactUs (): void {
    showContactUsModal.value = true;
}
</script>

<style scoped lang="scss">
.no-access-helper-wrapper {
    position: relative;
    display: flex;
    height: 100%;
    padding: var(--defaultSpacing);
    overflow: auto;

    .no-access-card {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--extraLargeSpacing);
        max-width: 54rem;
        padding: var(--extraLargeSpacing) 2.5rem;
        margin: auto;
        background-color: var(--neutral100);
        border-radius: var(--borderRadiusOuter);
        border: 1px solid var(--neutral300);
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
        z-index: 2;
    }

    .message-block {
        text-align: center;
        width: 100%;

        .text {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: var(--largeSpacing);
            font-size: var(--mediumFontSize);

            p {
                margin: 0;
                color: var(--neutral800);
                line-height: 1.5;
            }

            .highlight {
                color: var(--accentColour);
                font-weight: var(--boldFontWeight);
            }
        }

        .pun-subheading {
            font-size: var(--mediumFontSize);
            color: var(--accentColour);
            font-style: italic;
            margin-top: var(--extraSmallSpacing);
            margin-bottom: 0;
        }

        .contact-button {
            font-weight: var(--boldFontWeight);
        }
    }

    .character-block {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--extraLargeSpacing);
        flex-wrap: wrap;
        padding: var(--largeSpacing) var(--extraLargeSpacing);
        background-color: var(--neutral200);
        border-radius: var(--borderRadiusInner);
        width: 100%;
        box-sizing: border-box;

        .lock-icon {
            font-size: 4rem;
            color: var(--lcpSunrise);
            flex-shrink: 0;
        }

        .plug-boy-wrap {
            width: 7rem;
        }
    }

    .game-block {
        padding-top: var(--defaultSpacing);
        border-top: 1px solid var(--neutral300);

        .game-prompt {
            margin: 0 0 var(--defaultSpacing);
            font-size: var(--mediumFontSize);
            color: var(--accentColour);
            font-style: italic;
            text-align: center;
        }
    }

    .logo-container {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: 40vw;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;

        .spark-lines {
            position: absolute;
            width: 110%;
            z-index: 0;
            opacity: 0.4;
        }

        .spark-logo {
            position: absolute;
            z-index: 1;
            width: 30%;
            opacity: 0.9;
        }
    }
}
</style>
